import {
  EllipsedText,
  FlexColumn,
  FlexRow,
  LabeledData,
  MaterialIcon,
  useModal,
  useMoment,
} from '@gorila-shared-ui/components';
import { Badge } from 'baseui/badge';
import { Button } from 'baseui/button';
import { StyledLink } from 'baseui/link';
import { HeadingXSmall, LabelMedium, ParagraphSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { ASSET_TYPE_PHONE } from '../../constants/asset';
import { useCamera } from '../../hooks/useCamera';
import { useStyles } from '../../hooks/useStyles';
import { getJourney } from '../../services/journeyService';
import { apiParamsState } from '../../storage/app';
import { assetState } from '../../storage/assets';
import { currentStateState } from '../../storage/client';
import {
  assetLngLatsState,
  assetStartTimeState,
  averageSpeedState,
  currentAssetState,
  distanceTraveledState,
} from '../../storage/tracking';
import { Journey } from '../../types/journey';
import { getAssetTitle } from '../../utils/assets';
import { deviceIsCamera, isDevice } from '../../utils/currentStates';
import AssetModal from '../assets/modal/AssetModal';
import CommandModal from '../commands/modal/CommandModal';
import ConsumptionLabel from '../shared/ConsumptionLabel';
import OperationalStatusLabel from '../shared/OperationalStatusLabel';
import OperationalStatusTime from '../shared/OperationalStatusTime';
import { StyledTooltip } from '../ui/StyledTooltip';

export function TrackingInfo() {
  const { theme, classes, css } = useStyles();
  const { assetId } = useParams();
  const { formatDateFromTs } = useMoment();
  const apiParams = useRecoilValue(apiParamsState);
  const averageSpeed = useRecoilValue(averageSpeedState(assetId || ''));
  const distanceTraveled = useRecoilValue(distanceTraveledState(assetId || ''));
  const startTime = useRecoilValue(assetStartTimeState(assetId || ''));
  const asset = useRecoilValue(assetState(assetId || ''));
  const currentState = useRecoilValue(currentAssetState(assetId || ''));
  const isCamera = deviceIsCamera(
    useRecoilValue(
      currentStateState(isDevice(currentState?.asset.device) ? currentState.asset.device.ident : '') || '',
    ),
  );
  const assetLngLats = useRecoilValue(assetLngLatsState(assetId || ''));
  const { closeModal: closeCommandModal, openModal: openCommandModal, showModal: showCommandModal } = useModal();
  const { closeModal: closeAssetModal, openModal: openAssetModal, showModal: showAssetModal } = useModal();
  const { zoomTo, fitToLngLats } = useCamera();
  const [journey, setJourney] = useState<Journey>();
  const actionIconSize = theme.sizing.scale650;

  useEffect(() => {
    if (!apiParams || !asset?.journeyId) return;
    const loadJourney = async () => {
      const { journey, error } = await getJourney(apiParams.clientId, apiParams.subClientId, asset.journeyId);
      if (!error && journey) {
        setJourney(journey);
      } else {
        setJourney(undefined);
      }
    };
    loadJourney();
  }, [asset?.journeyId]);

  if (!asset) {
    return null;
  }

  return (
    <FlexColumn>
      <FlexColumn gap={theme.sizing.scale100}>
        <FlexColumn gap={0}>
          <FlexRow
            classNames={css({
              alignItems: 'center',
              justifyContent: 'space-between',
            })}
            gap={theme.sizing.scale400}
          >
            <FlexRow
              gap={theme.sizing.scale200}
              classNames={classes.centeredStart}
            >
              <LabelMedium
                margin={0}
                style={{ fontWeight: theme.typography.HeadingXSmall.fontWeight }}
              >
                <EllipsedText
                  inline
                  breakAll
                >
                  {getAssetTitle(asset)}
                </EllipsedText>
              </LabelMedium>
              {isCamera && currentState && (
                <ConsumptionLabel
                  gbConsumed={currentState.asset.gbConsumed}
                  percentageConsumed={currentState.asset.gbAssetConsumption}
                />
              )}
              {currentState?.operationalState && (
                <OperationalStatusTime operationalState={currentState.operationalState} />
              )}
              {currentState?.operationalState && (
                <OperationalStatusLabel
                  operationalState={currentState.operationalState}
                  positionDirection={currentState.position_direction}
                  speed={currentState.position_speed}
                  sleepMode={currentState.sleepModeStatus}
                />
              )}
            </FlexRow>
            <FlexRow gap={theme.sizing.scale100}>
              {asset && (
                <StyledTooltip content="Información del activo">
                  <Button
                    shape="circle"
                    kind="tertiary"
                    size="mini"
                    onClick={openAssetModal}
                  >
                    <MaterialIcon
                      name="info"
                      size={actionIconSize}
                    />
                  </Button>
                </StyledTooltip>
              )}
              {!!assetLngLats.length && (
                <StyledTooltip content="Centrar trayecto">
                  <Button
                    shape="circle"
                    kind="tertiary"
                    size="mini"
                    onClick={() => fitToLngLats(assetLngLats)}
                  >
                    <MaterialIcon
                      name="alt_route"
                      size={actionIconSize}
                    />
                  </Button>
                </StyledTooltip>
              )}
              {asset.assetType !== ASSET_TYPE_PHONE && (
                <StyledTooltip content="Enviar comando">
                  <Button
                    shape="circle"
                    kind="tertiary"
                    size="mini"
                    onClick={openCommandModal}
                  >
                    <MaterialIcon
                      name="settings_remote"
                      size={actionIconSize}
                    />
                  </Button>
                </StyledTooltip>
              )}
              {currentState && (
                <StyledTooltip content="Ubicación actual">
                  <Button
                    shape="circle"
                    kind="tertiary"
                    size="mini"
                    onClick={() =>
                      zoomTo({ lat: currentState.position_latitude, lng: currentState.position_longitude })
                    }
                  >
                    <MaterialIcon
                      name="location_searching"
                      size={actionIconSize}
                    />
                  </Button>
                </StyledTooltip>
              )}
            </FlexRow>
          </FlexRow>
          <ParagraphSmall
            margin={0}
            color={theme.colors.contentSecondary}
          >
            {asset.label}
          </ParagraphSmall>
        </FlexColumn>
        {asset.inJourney && (
          <Badge
            content="En trayecto"
            shape="rectangle"
            color="positive"
            overrides={{
              Badge: {
                style: {
                  width: 'fit-content',
                },
              },
            }}
          />
        )}
      </FlexColumn>
      <FlexColumn gap={theme.sizing.scale200}>
        <LabeledData
          label={'Inicio de seguimiento:'}
          content={startTime && formatDateFromTs(startTime)}
          labelWidth={'160px'}
        />
        <LabeledData
          label={'Velocidad promedio:'}
          content={averageSpeed}
          labelWidth={'160px'}
        />
        <LabeledData
          label={'Distancia recorrida:'}
          content={distanceTraveled}
          labelWidth={'160px'}
        />
      </FlexColumn>
      {asset.inJourney && journey && (
        <FlexColumn gap={theme.sizing.scale200}>
          <HeadingXSmall
            color={theme.colors.positive}
            margin={0}
          >
            Detalles del Trayecto
          </HeadingXSmall>
          <LabeledData
            label={'Salida:'}
            content={
              <StyledLink
                href={`https://maps.google.com/?q=${journey.startPoint?.lat},${journey.startPoint?.lng}`}
                target="_blank"
                style={{ ...theme.typography.ParagraphSmall }}
              >
                {journey.originAddress}
              </StyledLink>
            }
            labelWidth={'160px'}
          />
          <LabeledData
            label={'Hora de salida:'}
            content={!!journey?.startDate && formatDateFromTs(journey.startDate)}
            labelWidth={'160px'}
          />
          <LabeledData
            label={'Destino:'}
            content={
              <StyledLink
                href={`https://maps.google.com/?q=${journey.endPoint?.lat},${journey.endPoint?.lng}`}
                target="_blank"
                style={{ ...theme.typography.ParagraphSmall }}
              >
                {journey.destinationAddress}
              </StyledLink>
            }
            labelWidth={'160px'}
          />
          <LabeledData
            label={'Hora de llegada:'}
            content={!!journey?.endDate && formatDateFromTs(journey.endDate)}
            labelWidth={'160px'}
          />
        </FlexColumn>
      )}
      <CommandModal
        onClose={closeCommandModal}
        isOpen={showCommandModal}
        asset={asset}
      />
      <AssetModal
        onClose={closeAssetModal}
        isOpen={showAssetModal}
        assetId={asset._id}
      />
    </FlexColumn>
  );
}
